import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EDatePicker from '@/components/everridge/EDatePicker';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import { useUserActivitiesFilter } from '@/features/superAdmin/features/userActivities/hooks/useUserActivitiesFilter';
import {
    UserActivitiesPageFilterFormSchema,
    UserActivitiesPageFilterFormData,
} from '@/features/superAdmin/features/userActivities/validators/UserActivitiesPageFilterValidator';
import { UserActivitySource } from '@/features/superAdmin/features/userActivities/types/superAdmin-userActivities-dto';
import { useTenantsSelectOptionsApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsSelectOptionsApi';
import { useQuery } from '@tanstack/react-query';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { UserListResponseDto } from '@/features/superAdmin/features/users/types/superAdmin-users-dto';
import { Label } from '@/components/ui/label';

const TODAY = new Date();

type Props = {
    onClose: () => void;
    isMobile?: boolean;
};

const UserActivitiesPageFilter: FC<Props> = ({ onClose, isMobile }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();
    const axiosInstance = useAxiosAuth();
    const { filter, setFilter } = useUserActivitiesFilter();
    const { tenantsSelectData } = useTenantsSelectOptionsApi();

    const { data: usersData } = useQuery({
        queryKey: [ApiConfig.queries.superAdmin.users.queryKeys.list, 'userActivitiesFilter'],
        queryFn: async (): Promise<UserListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.users.endpoints.list,
                { take: 100, skip: 0 },
            );
            const response = await axiosInstance.get<UserListResponseDto>(url);
            return response.data;
        },
    });

    const { control, handleSubmit, reset, watch, setValue } = useForm<UserActivitiesPageFilterFormData>({
        resolver: zodResolver(UserActivitiesPageFilterFormSchema),
        values: {
            startDate: filter.startDate,
            endDate: filter.endDate,
            source: filter.source,
            userId: filter.userId,
            tenantId: filter.tenantId,
        },
    });

    const tenantOptions = useMemo(
        () => tenantsSelectData?.items ?? [],
        [tenantsSelectData?.items],
    );

    const userOptions = useMemo(() => usersData?.items ?? [], [usersData?.items]);

    const sourceSelectOptions = useMemo(
        () => [
            {
                value: String(UserActivitySource.App),
                label: t('main.superAdmin.userActivities.source.app'),
            },
            {
                value: String(UserActivitySource.Keycloak),
                label: t('main.superAdmin.userActivities.source.keycloak'),
            },
        ],
        [t],
    );

    const userSelectOptions = useMemo(
        () =>
            userOptions.map((user) => ({
                value: user.id,
                label: user.userName ?? user.email ?? user.id,
            })),
        [userOptions],
    );

    const tenantSelectOptions = useMemo(
        () =>
            tenantOptions.map((tenant) => ({
                value: tenant.id,
                label: tenant.name ?? tenant.id,
            })),
        [tenantOptions],
    );

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleSubmitFilterForm = (form: UserActivitiesPageFilterFormData) => {
        setFilter({
            startDate: form.startDate ?? filter.startDate,
            endDate: form.endDate ?? filter.endDate,
            source: form.source ?? null,
            userId: form.userId ?? null,
            tenantId: form.tenantId ?? null,
            skip: 0,
        });
        handleClose();
    };

    const handleResetFilter = () => {
        reset({
            startDate: TODAY,
            endDate: TODAY,
            source: null,
            userId: null,
            tenantId: null,
        });
    };

    const sourceValue = watch('source');
    const userIdValue = watch('userId');
    const tenantIdValue = watch('tenantId');

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {t('elements.components.filter.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                        <EDatePicker
                            control={control}
                            fieldName="startDate"
                            label={t('elements.form.startDate.label')}
                            placeholder={t('elements.form.startDate.placeholder')}
                        />
                        <EDatePicker
                            control={control}
                            fieldName="endDate"
                            label={t('elements.form.endDate.label')}
                            placeholder={t('elements.form.endDate.placeholder')}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>{t('main.superAdmin.userActivities.filter.source')}</Label>
                        <ESingleSelectPicker
                            value={
                                sourceValue === null || sourceValue === undefined
                                    ? null
                                    : String(sourceValue)
                            }
                            onChange={(value) =>
                                setValue(
                                    'source',
                                    value === null
                                        ? null
                                        : (Number(value) as UserActivitySource),
                                )
                            }
                            options={sourceSelectOptions}
                            placeholder={t(
                                'main.superAdmin.userActivities.filter.sourcePlaceholder',
                            )}
                            allOptionLabel={t(
                                'main.superAdmin.userActivities.filter.allSources',
                            )}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>{t('main.superAdmin.userActivities.filter.user')}</Label>
                        <ESingleSelectPicker
                            value={userIdValue}
                            onChange={(value) => setValue('userId', value)}
                            options={userSelectOptions}
                            placeholder={t(
                                'main.superAdmin.userActivities.filter.userPlaceholder',
                            )}
                            allOptionLabel={t(
                                'main.superAdmin.userActivities.filter.allUsers',
                            )}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>{t('main.superAdmin.userActivities.filter.tenant')}</Label>
                        <ESingleSelectPicker
                            value={tenantIdValue}
                            onChange={(value) => setValue('tenantId', value)}
                            options={tenantSelectOptions}
                            placeholder={t(
                                'main.superAdmin.userActivities.filter.tenantPlaceholder',
                            )}
                            allOptionLabel={t(
                                'main.superAdmin.users.form.tenantFilter.allTenants',
                            )}
                        />
                    </div>
                </div>
                <DialogFooter className={cn('justify-end', isMobile && 'mt-auto')}>
                    <Button variant="outline" onClick={handleResetFilter}>
                        {t('elements.buttons.reset')}
                    </Button>
                    <Button onClick={handleSubmit(handleSubmitFilterForm)}>
                        {t('elements.buttons.apply')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UserActivitiesPageFilter;
